import { Flex } from '@brightdrop/bd-ui';
import { TranslatedLabel, translateText } from '@gm-commercial/translations';
import {
  Box,
  Divider,
  type TableCellProps,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import clsx from 'clsx';
import {
  MaterialReactTable,
  type MRT_ColumnPinningState,
  type MRT_Icons,
  type MRT_Row,
  type MRT_RowData,
  type MRT_RowSelectionState,
  type MRT_TableOptions,
  MRT_TablePagination,
  useMaterialReactTable,
} from 'material-react-table';
import type React from 'react';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import SortDownArrowIcon from '../assets/icons/chevron-down.svg?react';
import SortUpDownArrowIcon from '../assets/icons/chevron-selector-vertical.svg?react';
import ColumnShowHideIcon from '../assets/icons/columnShowHideIcon.svg?react';
import EndOfRowIcon from '../assets/icons/dots-horizontal.svg?react';
import type { TableBannerProps } from '../models/selectedRowActionsBanner.model';
import type { RenderCardedRowProps } from '../models/table.model';
import CardedRowSkeleton from './CardedRowSkeleton';
import TableToolbar from './components/TableToolbar/TableToolbar';
import type { TableToolbarProps } from './components/TableToolbar/TableToolbar.model';
import RenderCardedRow from './RenderCardedRow';
import TableBanner from './SelectedRowActionsBanner/SelectedRowActionsBanner';
import useStyles from './Table.styles';
import {
  getTableResultSetKey,
  TABLE_MOBILE_SCROLL_CONTAINER,
  useScrollTableToTopOnResultSetChange,
} from './useScrollTableToTopOnResultSetChange';

export type TableProps<T extends MRT_RowData> = {
  tablePropsObj: MRT_TableOptions<T>;
  tableToolbarConfig?: TableToolbarProps<unknown>;
  renderTableCountLabel?: {
    translationKey: string;
    variables?: { [key: string]: string | number };
  };
  renderSearchComponent?: ReactNode;
  renderShowHideColumnsComponent?: ReactNode;
  renderFilterComponent?: ReactNode;
  renderFilterSummary?: ReactNode;
  renderAdminActionsComponent?: ReactNode;
  enableMobileViewLayout?: boolean;
  renderNoRecordFound?: ReactNode;
  onRowClick?: (
    event: React.MouseEvent<HTMLDivElement>,
    row: MRT_Row<T>
  ) => void;
  disableManualPagination?: boolean;
  renderAddItemsComponent?: ReactNode;
  renderAccordionMobileView?: boolean; // 0.7 mobile view
  mobileCardHeaderColumnIndex?: number; // The column index to target for mobile view row card header, next to the selection checkbox.
  renderBanner?: TableBannerProps;
  isPaginationDisabled?: boolean; // When true, disables pagination controls to prevent rapid clicking during data fetch operations
  hideCardedRowCheckbox?: boolean;
  showRowActionMenuItems?: boolean;
  /**
   * Scrolls row content inside the table body so toolbar, banner, and pagination
   * chrome stay visible (used inside height-constrained modals).
   */
  containedScroll?: boolean;
  /**
   * Extra identity for scroll-to-top (e.g. search + filters). Changes reset
   * scroll even when `pageIndex` stays 0 and the lead row/count are unchanged.
   */
  scrollResetKey?: string;
};

/**
 * A flexible table component that supports both standard and card-style layouts.
 *
 * This component uses Material React Table and allows customizing the column layout
 * at the column definition level. You can enable a card-style layout by passing the
 * `enableCardLayout` prop.
 *
 * @template T - The type of data represented by each row.
 *
 * @param {Object} props - The component props.
 * @param {MRT_TableOptions<T>} props.tablePropsObj - The configuration options for the Material React Table.
 * @param {boolean} [props.enableCardLayout=false] - Whether to display the table in a card-style layout.
 * @param {boolean} props.disableManualPagination - Set to true if wanting client-side pagination instead of server-side pagination.
 *
 * @returns {JSX.Element} - The rendered table component.
 *
 * @example
 * ```tsx
 * import Table from './Table';
 * import { NAME_COLUMN_DEF, DIAGNOSTICS_COLUMN_DEF } from './columnDefinitions';
 *
 * const columns = [
 *   NAME_COLUMN_DEF(),
 *   DIAGNOSTICS_COLUMN_DEF('Critical Diagnostics'),
 * ];
 *
 * const tablePropsObj = {
 *   columns,
 *   data: yourData,
 * };
 *
 * <Table tablePropsObj={tablePropsObj} enableCardLayout={true} />;
 * ```
 */
const Table = <T extends MRT_RowData>(props: TableProps<T>): JSX.Element => {
  const { classes } = useStyles();
  const theme = useTheme();
  const isMobileView = useMediaQuery(theme.breakpoints.down('lg'));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10, //customize the default page size
  });

  // Include isPaginationDisabled in loading state to show skeleton during data fetch operations
  // when pagination is disabled to prevent rapid clicking
  const isLoading =
    props.tablePropsObj.state?.isLoading || props.isPaginationDisabled;
  const shouldUseMobileLayout = isMobileView && props.enableMobileViewLayout;

  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  const [columnPinning, setColumnPinning] = useState<MRT_ColumnPinningState>({
    left: [],
    right: [],
  });
  const CustomIcons: Partial<MRT_Icons> = {
    ArrowDownwardIcon: (iconProps: React.ComponentProps<'svg'>) => (
      <SortDownArrowIcon
        {...iconProps}
        className={[classes.downSortIcon, iconProps.className]
          .filter(Boolean)
          .join(' ')}
      />
    ),
    SyncAltIcon: () => (
      <SortUpDownArrowIcon className={classes.defaultSortIcon} />
    ),
    ViewColumnIcon: () => <ColumnShowHideIcon />,
    MoreHorizIcon: () => <EndOfRowIcon />,
  };

  /* Ref of the table to be used in the banner component for resizing purposes*/
  const tableRef = useRef<HTMLDivElement>(null);

  const updatedTablePropsObj: MRT_TableOptions<T> = {
    enableColumnActions: false,
    enableRowActions: false,
    enableColumnPinning: false,
    enableRowSelection: false,
    enableSelectAll: false,
    enableTopToolbar: true,
    enableToolbarInternalActions: true,
    enableBottomToolbar: props.tablePropsObj.data.length > 0,
    enablePagination: props.tablePropsObj.data.length > 0,
    enableSorting: props.tablePropsObj.enableSorting ?? false,
    manualPagination: !props.disableManualPagination,
    manualFiltering: true,
    positionPagination: 'bottom',
    layoutMode: 'semantic',
    positionActionsColumn: 'last',
    icons: CustomIcons,
    muiSelectCheckboxProps: {
      title: translateText('common:select'),
    },
    muiSelectAllCheckboxProps: {
      title: translateText('common:select'),
    },
    initialState: {
      columnPinning,
      ...props.tablePropsObj.initialState,
    },
    muiPaginationProps: {
      showRowsPerPage: false,
      showFirstButton: false,
      showLastButton: false,
      disabled: props.isPaginationDisabled || false,
    },
    muiBottomToolbarProps: {
      sx: {
        padding: '24px 16px',
        boxShadow: 'none',
      },
    },
    displayColumnDefOptions: {
      'mrt-row-select': {
        size: 50,
        grow: false,
        muiTableHeadCellProps: {
          sx: {
            justifyContent: 'center',
          },
        },
      },
      'mrt-row-actions': {
        size: 50,
        grow: false,
        header: '',
      },
    },
    muiSearchTextFieldProps: {
      size: 'small',
      variant: 'outlined',
    },
    // Merging custom styles with the provided muiTableBodyRowProps
    muiTableBodyRowProps: (rowProps) => {
      const defaultRowProps = {
        hover: false,
        sx: {
          display: props.enableMobileViewLayout
            ? { xs: 'block', lg: 'table-row' }
            : 'table-row',
        },
      };

      // Check if tablePropsObj has muiTableBodyRowProps and apply it
      const additionalRowProps =
        typeof props.tablePropsObj.muiTableBodyRowProps === 'function'
          ? props.tablePropsObj.muiTableBodyRowProps(rowProps)
          : props.tablePropsObj.muiTableBodyRowProps || {};

      // combined styles
      return {
        ...defaultRowProps,
        ...additionalRowProps,
        sx: {
          ...defaultRowProps.sx,
          ...(additionalRowProps.sx || {}),
        },
      };
    },
    // Merging custom styles with the provided muiTableBodyCellProps
    muiTableBodyCellProps: (cellProps) => {
      const defaultCellProps: TableCellProps = {
        sx: {
          display: props.enableMobileViewLayout
            ? { xs: 'flex', lg: 'table-cell' }
            : 'table-cell',
          flexDirection: props.enableMobileViewLayout
            ? { xs: 'column', lg: 'row' }
            : 'row',
        },
      };

      // Check if tablePropsObj has muiTableBodyCellProps and apply it
      const additionalCellProps =
        typeof props.tablePropsObj.muiTableBodyCellProps === 'function'
          ? props.tablePropsObj.muiTableBodyCellProps(cellProps)
          : props.tablePropsObj.muiTableBodyCellProps || {};

      // Merge styles
      return {
        ...defaultCellProps,
        ...additionalCellProps,
        sx: {
          ...defaultCellProps.sx,
          ...(additionalCellProps.sx || {}),
        },
      };
    },
    muiTableContainerProps: {
      sx: {
        maxWidth: '100%',
      },
    },
    onRowSelectionChange: setRowSelection,
    onColumnPinningChange: setColumnPinning,
    ...(props.disableManualPagination
      ? {}
      : { onPaginationChange: setPagination }),
    ...props.tablePropsObj,
    state: {
      ...(props.disableManualPagination ? {} : pagination),
      rowSelection,
      columnPinning,
      ...props.tablePropsObj.state,
    },
    // first click on an unsorted column sorts ascending (sortDescFirst: false). Not overridable.
    defaultColumn: (() => {
      const { defaultColumn, enableSorting } = props.tablePropsObj;
      const { sortDescFirst: _ignoredSortDescFirst, ...restDefaultColumn } =
        defaultColumn ?? {};
      return {
        size: 110,
        ...restDefaultColumn,
        ...(enableSorting ? { sortDescFirst: false } : {}),
      };
    })(),
    renderTopToolbar: () =>
      props.tableToolbarConfig ? (
        <>
          <TableToolbar {...props.tableToolbarConfig} />
          {props.renderBanner && (
            <TableBanner
              {...props.renderBanner}
              isLoading={props.tablePropsObj.state?.isLoading}
              tableRef={tableRef}
            />
          )}
        </>
      ) : (
        <>
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              justifyContent: 'space-between',
              overflowX: 'scroll',
              padding:
                props.renderSearchComponent ||
                props.renderFilterComponent ||
                props.renderAdminActionsComponent ||
                props.renderTableCountLabel ||
                props.renderShowHideColumnsComponent
                  ? '12px'
                  : 0,
              scrollbarWidth: 'thin',
              scrollbarColor: `${theme.new.color.grey[300]} ${theme.new.color.common.white}`,
            }}
          >
            <Flex
              col
              style={{ justifyContent: 'start', alignItems: 'flex-start' }}
            >
              {props.renderTableCountLabel && (
                <TranslatedLabel
                  translationKey={props.renderTableCountLabel.translationKey}
                  typographyVariant="body_medium"
                  variables={props.renderTableCountLabel.variables}
                  dataTestId="table-total-count-label"
                />
              )}

              {props.renderFilterSummary && (
                <Flex col style={{ alignItems: 'flex-start', gap: '4px' }}>
                  {props.renderFilterSummary}
                </Flex>
              )}
            </Flex>
            <Flex center gap={12}>
              <Flex>{props.renderAdminActionsComponent}</Flex>
              {props.renderSearchComponent}
              {props.renderFilterComponent}
              {props.renderShowHideColumnsComponent && (
                <>
                  <Divider
                    orientation="vertical"
                    flexItem
                    className={classes.verticalDivider}
                  />
                  {props.renderShowHideColumnsComponent}
                  <Divider
                    orientation="vertical"
                    flexItem
                    className={classes.verticalDivider}
                  />
                </>
              )}
              {props.renderAddItemsComponent &&
                props.renderAccordionMobileView && (
                  <Divider
                    orientation="vertical"
                    flexItem
                    className={classes.verticalDivider}
                  />
                )}
              {props.renderAddItemsComponent}
            </Flex>
          </Box>
          {props.renderBanner && (
            <TableBanner
              {...props.renderBanner}
              isLoading={props.tablePropsObj.state?.isLoading}
              tableRef={tableRef}
            />
          )}
        </>
      ),
    renderEmptyRowsFallback: () => (
      <Box py={'48px'} px={'12px'} display={'flex'} justifyContent={'center'}>
        {props.renderNoRecordFound ? (
          props.renderNoRecordFound
        ) : (
          <TranslatedLabel
            translationKey={'common:noResult'}
            typographyVariant={'header2_medium'}
          />
        )}
      </Box>
    ),
  };

  // Use timeout to prevent race conditions during loading transitions for mobile view
  const [showData, setShowData] = useState(!isLoading);

  useEffect(() => {
    if (shouldUseMobileLayout) {
      if (isLoading) {
        setShowData(false);
      } else {
        // Add a small delay when loading stops to prevent race conditions
        const timer = setTimeout(() => {
          setShowData(true);
        }, 100);
        return () => clearTimeout(timer);
      }
    } else {
      setShowData(true);
    }
  }, [isLoading, shouldUseMobileLayout]);

  const table = useMaterialReactTable(updatedTablePropsObj);

  const pageIndex = table.getState().pagination.pageIndex;
  const tableData = table.options.data;
  const rowCount = table.options.rowCount ?? tableData.length;
  // Prefer a stable original id: TanStack defaults `row.id` to the page index
  // ("0"), which would miss result-set changes when pageIndex/rowCount stay put.
  const leadRow = table.getRowModel().rows[0];
  const leadOriginalId = (
    leadRow?.original as { id?: string | number } | undefined
  )?.id;
  const leadRowId =
    leadOriginalId != null ? String(leadOriginalId) : (leadRow?.id ?? '');

  useScrollTableToTopOnResultSetChange(
    tableRef,
    getTableResultSetKey(
      pageIndex,
      rowCount,
      leadRowId,
      props.scrollResetKey ?? ''
    )
  );

  // Currently only two instances of this configuration are used:
  // 1. Vehicle Boarding History and 2. Home Charging Session List
  const isMobileViewWithFiltersAndLabelOnly =
    !!props.renderTableCountLabel &&
    !!props.renderFilterSummary &&
    !props.renderSearchComponent &&
    !!props.renderFilterComponent &&
    !props.renderAddItemsComponent &&
    !props.renderAdminActionsComponent;

  const defaultToolbarContent = (): JSX.Element => {
    return (
      <>
        <Flex col style={{ alignItems: 'flex-start' }}>
          {props.renderTableCountLabel && (
            <TranslatedLabel
              translationKey={props.renderTableCountLabel.translationKey}
              typographyVariant="body_medium"
              variables={props.renderTableCountLabel.variables}
              dataTestId="table-total-count-label"
            />
          )}
          {props.renderFilterSummary && (
            <Flex col style={{ alignItems: 'flex-start', gap: '4px' }}>
              {props.renderFilterSummary}
            </Flex>
          )}
        </Flex>
        <Box className={classes.searchFilterWrapper}>
          {props.renderAdminActionsComponent}
          <Flex className={classes.toolbarActions}>
            <Flex style={{ flex: '1 0 0', gap: '12px' }}>
              {props.renderSearchComponent}
              {props.renderFilterComponent}
            </Flex>
            {props.renderAddItemsComponent &&
              !isSmallScreen &&
              props.renderAccordionMobileView && (
                <Divider
                  orientation="vertical"
                  flexItem
                  className={classes.verticalDivider}
                />
              )}
            <Flex>{props.renderAddItemsComponent}</Flex>
          </Flex>
        </Box>
      </>
    );
  };

  /**
   * Mobile card rows for the current page. getPaginationRowModel() applies
   * client-side paging when manualPagination is false (disableManualPagination)
   * and returns the full current data slice when manualPagination is true
   * (server-side). Do not slice again with local pagination state — TanStack
   * already paginates here, and a manual slice breaks later pages.
   */
  const mobileDisplayRows = table.getPaginationRowModel().rows;
  const mobilePageSize = table.getState().pagination.pageSize;

  const skeletonCards = Array.from({ length: mobilePageSize }, (_, index) => ({
    id: `mobile-card-skeleton-${index}`,
  }));

  const mobileCardRows =
    isLoading || !showData
      ? skeletonCards.map((skeleton) => (
          <CardedRowSkeleton
            key={skeleton.id}
            renderAccordionMobileView={props.renderAccordionMobileView}
            hideCardedRowCheckbox={props.hideCardedRowCheckbox}
          />
        ))
      : mobileDisplayRows.map((row) => {
          // material-react-table's row/column/table generics are built on a
          // deeply recursive `DeepKeys` type. The native TS compiler resolves
          // those to distinct identities across this component boundary even
          // though the runtime shapes are identical, so the props are asserted
          // to the shape RenderCardedRow consumes.
          const cardedRowProps = {
            row,
            columns: props.tablePropsObj.columns,
            onClick: props.onRowClick,
            table,
            renderAccordionMobileView: props.renderAccordionMobileView,
            mobileTargetColumnIndex: props.mobileCardHeaderColumnIndex,
            hideCardedRowCheckbox: props.hideCardedRowCheckbox,
            renderRowActionMenuItems:
              props.tablePropsObj.renderRowActionMenuItems,
            showRowActionMenuItems: props.showRowActionMenuItems,
          } as RenderCardedRowProps<T>;

          return <RenderCardedRow key={row.id} {...cardedRowProps} />;
        });

  return shouldUseMobileLayout ? (
    <Box
      className={clsx(
        classes.mobileViewWidget,
        props.containedScroll && classes.mobileViewWidgetContained
      )}
      ref={tableRef}
      data-testid="table-mobile-view"
    >
      <Box
        className={clsx(
          classes.searchFilterDiv,
          props.containedScroll && classes.mobileViewStickyHeader
        )}
      >
        {props.tableToolbarConfig ? (
          <TableToolbar {...props.tableToolbarConfig} />
        ) : isMobileViewWithFiltersAndLabelOnly ? (
          <Box
            display="flex"
            flexDirection="row"
            alignItems="center"
            justifyContent="space-between"
            data-testid="mobile-label-filter-div"
          >
            {defaultToolbarContent()}
          </Box>
        ) : (
          defaultToolbarContent()
        )}
      </Box>
      {props.renderBanner && (
        <TableBanner {...props.renderBanner} tableRef={tableRef} />
      )}
      {props.containedScroll ? (
        <Box
          className={classes.mobileScrollBody}
          data-scroll-container={TABLE_MOBILE_SCROLL_CONTAINER}
          data-testid="table-mobile-scroll-body"
        >
          {mobileCardRows}
        </Box>
      ) : (
        mobileCardRows
      )}
      <Box
        className={
          props.containedScroll ? classes.mobilePaginationFooter : undefined
        }
      >
        <MRT_TablePagination table={table} />
      </Box>
    </Box>
  ) : (
    <div
      className={classes.table}
      ref={tableRef}
      id="table-list-view"
      data-testid="table-list-view"
    >
      <MaterialReactTable table={table} />
    </div>
  );
};

export { useMaterialReactTable };
export default Table;
